import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { migrateAuthStorageKey } from './migrate-storage-key';
import type { Database } from './supabase.generated';

export type SupabaseClientRuntimeConfig = {
  supabaseUrl: string;
  supabaseAnonKey: string;
  storageKey?: string;
  /**
   * Previous `storageKey`, if this app has been renamed. The session is moved to
   * `storageKey` before the client is built, so a rename does not sign anyone out.
   * See `migrate-storage-key.ts`. Safe to drop once no live session predates the
   * rename.
   */
  migrateFromStorageKey?: string;
};

const DEFAULT_STORAGE_KEY = 'pyetro-auth';

let sharedSupabaseClient: SupabaseClient<Database> | null = null;

export const createSupabaseClient = ({
  supabaseUrl,
  supabaseAnonKey,
  storageKey,
  migrateFromStorageKey,
}: SupabaseClientRuntimeConfig): SupabaseClient<Database> => {
  const key = storageKey ?? DEFAULT_STORAGE_KEY;

  // Must run BEFORE createClient: supabase-js reads the stored session during
  // construction, so moving the value afterwards would be a boot too late and the
  // user would see a login screen once.
  if (migrateFromStorageKey && migrateFromStorageKey !== key) {
    migrateAuthStorageKey(migrateFromStorageKey, key);
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      storageKey: key,
    },
  });
};

export const initSupabaseClient = (
  config: SupabaseClientRuntimeConfig,
): SupabaseClient<Database> => {
  if (sharedSupabaseClient) {
    return sharedSupabaseClient;
  }

  sharedSupabaseClient = createSupabaseClient(config);
  return sharedSupabaseClient;
};

const getEnvConfig = (): SupabaseClientRuntimeConfig => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase runtime env vars (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY).',
    );
  }

  return {
    supabaseUrl,
    supabaseAnonKey,
  };
};

export const getSupabaseClient = (): SupabaseClient<Database> => {
  if (!sharedSupabaseClient) {
    return initSupabaseClient(getEnvConfig());
  }

  return sharedSupabaseClient;
};

const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_, propertyKey) {
    const client = getSupabaseClient();
    const value = Reflect.get(client, propertyKey);

    if (typeof value === 'function') {
      return value.bind(client);
    }

    return value;
  },
});

export default supabase;
